import { Context, Data, Effect, Layer } from "effect";
import type { InvoiceConfig } from "../types";
import { RepositoryError } from "../shared";

/**
 * When the invoice format is wrong
 */
export class InvoiceNumberFormatError extends Data.TaggedError("@/InvoiceNumberFormatError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly expected?: string;
  readonly received?: string;
  readonly diff?: string;
}> {}

/**
 * Domain service for validating invoice number formats according to business rules.
 *
 * Has no I/O. Can be (and should only be) used in the Domain.
 *
 * Validates:
 * - Format compliance (INV-1001 or 1221 patterns)
 */
export interface InvoiceNumberFormatValidatorSchema {
  /**
   * Validates invoice number format
   *
   * @param invoiceNumber - The invoice number to validate
   * @returns Effect that succeeds if valid, fails with appropriate error if invalid
   */
  readonly validate: (
    // eslint-disable-next-line no-unused-vars
    invoiceNumber: string
  ) => Effect.Effect<void, InvoiceNumberFormatError | RepositoryError>;
}

/**
 * Context tag for InvoiceNumberFormatValidator service
 */
export class InvoiceNumberFormatValidator extends Context.Tag("@/InvoiceNumberFormatValidator")<
  InvoiceNumberFormatValidator,
  InvoiceNumberFormatValidatorSchema
>() {}

/**
 * Context tag for InvoiceConfig
 */
export const InvoiceConfigTag = Context.GenericTag<InvoiceConfig>("@/InvoiceConfig");

/**
 * Implementation of InvoiceNumberFormatValidator service
 */
export const InvoiceNumberFormatValidatorLive = Layer.effect(
  InvoiceNumberFormatValidator,
  Effect.gen(function* () {
    const invoiceConfig = yield* InvoiceConfigTag;

    const implementation: InvoiceNumberFormatValidatorSchema = {
      validate: (invoiceNumber: string) => {
        return Effect.gen(function* () {
          // Check if invoice number is empty or null
          if (!invoiceNumber || invoiceNumber.trim().length === 0) {
            return yield* Effect.fail(
              new InvoiceNumberFormatError({
                message: "Invoice number cannot be empty",
              })
            );
          }

          const trimmedNumber = invoiceNumber.trim();

          // Validate format based on configuration and backward compatibility
          const isValid = yield* validateInvoiceNumberFormat(trimmedNumber, invoiceConfig);

          if (!isValid) {
            const expectedFormats = getExpectedFormats(invoiceConfig);
            return yield* Effect.fail(
              new InvoiceNumberFormatError({
                message: `Invalid invoice number format: '${invoiceNumber}'. Expected formats: ${expectedFormats}`,
              })
            );
          }

          // Validate numeric part is greater than 0
          const numericPart = extractNumericPart(trimmedNumber);
          if (numericPart <= 0) {
            return yield* Effect.fail(
              new InvoiceNumberFormatError({
                message: `Invalid invoice number: '${invoiceNumber}'. Numeric part must be greater than 0`,
              })
            );
          }
        });
      },
    };
    return InvoiceNumberFormatValidator.of(implementation);
  })
);

/**
 * Validates invoice number format based on configuration and backward compatibility.
 *
 * The following criteria are checked:
 * - Always allow numeric (see deprecation notice)
 * - If prefix configured, prefix must be set with a dash and a numeric value (e.g. "PREFIX-1234")
 * - If no prefix configured, the invoice number must be numeric
 *
 * **Deprecation Notice**: starting September 1st, we no longer accept backward
 * compatibility logic. This means, the invoice number as pure numeric will be only
 * valid if there is no prefix set.
 */
const validateInvoiceNumberFormat = (
  invoiceNumber: string,
  config: InvoiceConfig
): Effect.Effect<boolean, never> => {
  // Always accept numeric-only format for backward compatibility
  if (/^\d+$/.test(invoiceNumber)) {
    return Effect.succeed(true);
  }

  // If no prefix is configured, only accept numeric format
  if (!config.prefix) {
    return Effect.succeed(/^\d+$/.test(invoiceNumber));
  }

  // If prefix is configured, accept both numeric and prefixed formats
  const prefixPattern = new RegExp(`^${escapeRegex(config.prefix)}-\\d+$`);
  return Effect.succeed(prefixPattern.test(invoiceNumber) || /^\d+$/.test(invoiceNumber));
};

/**
 * Extracts the numeric part from an invoice number
 */
const extractNumericPart = (invoiceNumber: string): number => {
  // If it's numeric-only, return as is
  if (/^\d+$/.test(invoiceNumber)) {
    return parseInt(invoiceNumber, 10);
  }

  // If it has a prefix, extract the part after the last hyphen
  const parts = invoiceNumber.split("-");
  if (parts.length > 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const numericPart = parts[parts.length - 1]!;
    return parseInt(numericPart, 10);
  }

  return 0; // Invalid format
};

/**
 * Gets expected formats description for error messages
 */
const getExpectedFormats = (config: InvoiceConfig): string => {
  if (!config.prefix) {
    return "numeric format (e.g., '1001')";
  }

  return `numeric format (e.g., '1001') or prefixed format (e.g., '${config.prefix}-1001')`;
};

/**
 * Escapes special regex characters in a string
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};
